
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Save, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { getVoucherSettings, saveVoucherSettings, VoucherSettings, getAllVoucherTypes, saveVoucherType, VoucherType } from "@/lib/db/voucherDb";

const VoucherSettingsTab: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [voucherTypes, setVoucherTypes] = useState<VoucherType[]>([]);
  const [newType, setNewType] = useState<{
    name: string;
    description: string;
    maxAmount: number;
  }>({
    name: "",
    description: "",
    maxAmount: 100
  });
  const { toast } = useToast();
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<VoucherSettings>({
    defaultValues: {
      id: "voucher-settings",
      dailyCap: 1000,
      weeklyCap: 5000,
      notifyOnAssignment: true,
      smsTemplate: "",
      emailTemplate: ""
    }
  });
  
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        const settings = await getVoucherSettings();
        if (settings) {
          setValue("dailyCap", settings.dailyCap);
          setValue("weeklyCap", settings.weeklyCap);
          setValue("notifyOnAssignment", settings.notifyOnAssignment);
          setValue("smsTemplate", settings.smsTemplate);
          setValue("emailTemplate", settings.emailTemplate);
        }
        
        const types = await getAllVoucherTypes();
        setVoucherTypes(types);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading voucher settings:", error);
        toast({
          variant: "destructive",
          title: "Failed to load settings",
          description: "Could not retrieve voucher settings. Please try again."
        });
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, [setValue, toast]);
  
  const onSubmit = async (data: VoucherSettings) => {
    setIsSaving(true);
    try {
      await saveVoucherSettings({
        ...data,
        id: "voucher-settings" // Ensure we're always using the same ID
      });
      
      toast({
        title: "Settings Saved",
        description: "Voucher settings have been updated successfully."
      });
    } catch (error) {
      console.error("Error saving voucher settings:", error);
      toast({
        variant: "destructive",
        title: "Failed to save settings",
        description: "Could not save voucher settings. Please try again."
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleNewTypeChange = (key: string, value: string | number) => {
    setNewType(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const addNewType = async () => {
    if (!newType.name || !newType.description || newType.maxAmount <= 0) {
      toast({
        variant: "destructive",
        title: "Invalid Type",
        description: "Please fill in all fields with valid values."
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const typeId = newType.name.toLowerCase().replace(/\s+/g, '-');
      
      const newVoucherType: VoucherType = {
        typeId,
        name: newType.name,
        description: newType.description,
        maxAmount: newType.maxAmount
      };
      
      await saveVoucherType(newVoucherType);
      
      // Refresh the list
      const types = await getAllVoucherTypes();
      setVoucherTypes(types);
      
      // Reset form
      setNewType({
        name: "",
        description: "",
        maxAmount: 100
      });
      
      toast({
        title: "Type Added",
        description: `Voucher type "${newType.name}" has been added successfully.`
      });
    } catch (error) {
      console.error("Error adding voucher type:", error);
      toast({
        variant: "destructive",
        title: "Failed to add type",
        description: "Could not add voucher type. Please try again."
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle>General Settings</CardTitle>
          <CardDescription>
            Configure global settings for the voucher management system.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Voucher Caps</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dailyCap">Daily Voucher Cap (RM)</Label>
                  <Input
                    id="dailyCap"
                    type="number"
                    min="0"
                    step="100"
                    {...register("dailyCap", { 
                      required: "Daily cap is required",
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: "Daily cap must be a positive number"
                      }
                    })}
                  />
                  {errors.dailyCap && (
                    <p className="text-sm text-destructive">{errors.dailyCap.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Maximum amount of vouchers that can be issued in a single day
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="weeklyCap">Weekly Voucher Cap (RM)</Label>
                  <Input
                    id="weeklyCap"
                    type="number"
                    min="0"
                    step="100"
                    {...register("weeklyCap", { 
                      required: "Weekly cap is required",
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: "Weekly cap must be a positive number"
                      }
                    })}
                  />
                  {errors.weeklyCap && (
                    <p className="text-sm text-destructive">{errors.weeklyCap.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Maximum amount of vouchers that can be issued in a week
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Notifications</h3>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="notifyOnAssignment"
                  checked={watch("notifyOnAssignment")}
                  onCheckedChange={(checked) => setValue("notifyOnAssignment", checked)}
                />
                <Label htmlFor="notifyOnAssignment">
                  Send notification to customer when a voucher is assigned
                </Label>
              </div>
              
              {watch("notifyOnAssignment") && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="smsTemplate">SMS Template</Label>
                    <Textarea
                      id="smsTemplate"
                      placeholder="You have been assigned a {voucherType} voucher worth RM{amount}. Valid until {expiryDate}. Your voucher code is {voucherCode}."
                      className="min-h-[80px]"
                      {...register("smsTemplate")}
                    />
                    <p className="text-xs text-muted-foreground">
                      Available placeholders: {"{customerName}"}, {"{voucherType}"}, {"{amount}"}, {"{expiryDate}"}, {"{voucherCode}"}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="emailTemplate">Email Template</Label>
                    <Textarea
                      id="emailTemplate"
                      placeholder="Dear {customerName},

You have been assigned a {voucherType} voucher worth RM{amount}. This voucher is valid until {expiryDate}.

Your voucher code is {voucherCode}.

Thank you,
JiMATPOS Team"
                      className="min-h-[160px]"
                      {...register("emailTemplate")}
                    />
                    <p className="text-xs text-muted-foreground">
                      Available placeholders: {"{customerName}"}, {"{voucherType}"}, {"{amount}"}, {"{expiryDate}"}, {"{voucherCode}"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </CardFooter>
        </form>
      </Card>
      
      {/* Voucher Types */}
      <Card>
        <CardHeader>
          <CardTitle>Voucher Types</CardTitle>
          <CardDescription>
            Manage the types of vouchers that can be issued in your store.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Maximum Amount (RM)</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center">
                      Loading voucher types...
                    </TableCell>
                  </TableRow>
                ) : voucherTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center">
                      No voucher types defined yet
                    </TableCell>
                  </TableRow>
                ) : (
                  voucherTypes.map((type) => (
                    <TableRow key={type.typeId}>
                      <TableCell className="font-medium">{type.name}</TableCell>
                      <TableCell>{type.description}</TableCell>
                      <TableCell>RM {type.maxAmount}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Trash2 className="h-4 w-4 text-destructive" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                
                {/* Add new type row */}
                <TableRow>
                  <TableCell>
                    <Input 
                      placeholder="Type Name" 
                      value={newType.name} 
                      onChange={(e) => handleNewTypeChange("name", e.target.value)} 
                    />
                  </TableCell>
                  <TableCell>
                    <Input 
                      placeholder="Description" 
                      value={newType.description} 
                      onChange={(e) => handleNewTypeChange("description", e.target.value)} 
                    />
                  </TableCell>
                  <TableCell>
                    <Input 
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Max Amount" 
                      value={newType.maxAmount}
                      onChange={(e) => handleNewTypeChange("maxAmount", parseInt(e.target.value))} 
                    />
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full" 
                      onClick={addNewType}
                      disabled={isSaving}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoucherSettingsTab;
